import React, { useState, useEffect, useRef } from 'react';

interface InlineValueEditorProps {
  value: number;
  units: string;
  originalValue?: number;
  originalUnits?: string;
  testName: string;
  date: string;
  position?: { x: number; y: number };
  referenceRange?: { min?: number; max?: number };
  availableUnits?: string[];  // All units used for this test
  isSuspicious?: boolean;
  validationMessage?: string;
  onSave: (newValue: number, newUnit?: string) => void;
  onCancel: () => void;
  onMarkAsCorrect?: () => void;
}

export const InlineValueEditor: React.FC<InlineValueEditorProps> = ({
  value,
  units,
  originalValue,
  originalUnits,
  testName,
  date,
  position,
  referenceRange,
  availableUnits,
  isSuspicious,
  validationMessage,
  onSave,
  onCancel,
  onMarkAsCorrect
}) => {
  // Check if we have original units different from current units
  const hasOriginalUnits = originalValue !== undefined && originalUnits && 
                          originalUnits.toLowerCase() !== units.toLowerCase();
  
  // Default to showing original value/units if available
  const [useOriginal, setUseOriginal] = useState(false); // Don't default to original, use the passed value
  const [inputValue, setInputValue] = useState(value ? value.toString() : '');
  const [warning, setWarning] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Get the current display unit
  const currentUnit = useOriginal && hasOriginalUnits ? originalUnits! : units;

  useEffect(() => {
    // Focus input on mount
    inputRef.current?.focus();
    inputRef.current?.select();

    // Handle clicks outside
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  const validateValue = (val: number) => {
    setWarning(null);
    
    if (referenceRange) {
      if (referenceRange.min !== undefined && val < referenceRange.min) {
        setWarning(`Below normal range (min: ${referenceRange.min})`);
      } else if (referenceRange.max !== undefined && val > referenceRange.max) {
        setWarning(`Above normal range (max: ${referenceRange.max})`);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      validateValue(numValue);
    }
  };

  const handleSave = () => {
    const newValue = parseFloat(inputValue);
    if (isNaN(newValue)) {
      setWarning('Please enter a valid number');
      return;
    }
    
    onSave(newValue, currentUnit);
  };
  
  const toggleUnits = () => {
    if (!hasOriginalUnits) return;
    
    const newUseOriginal = !useOriginal;
    setUseOriginal(newUseOriginal);
    
    // Update the displayed value when toggling
    if (newUseOriginal && originalValue !== undefined) {
      setInputValue(originalValue.toString());
    } else {
      setInputValue(value.toString());
    }
    
    // Clear any warnings when switching
    setWarning(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const editorStyle = position ? {
    position: 'fixed' as const,
    left: position.x,
    top: position.y,
    transform: 'translate(-50%, -120%)',
    zIndex: 9999, // Ensure it's on top
  } : {};

  return (
    <div 
      ref={containerRef}
      className="inline-value-editor"
      style={editorStyle}
    >
      <div className="editor-arrow"></div>
      <div className="editor-content">
        <div className="editor-header">
          <span className="test-name">{testName}</span>
          <span className="date">{date}</span>
        </div>
        
        <div className="editor-input-group">
          <input
            ref={inputRef}
            type="number"
            step="any"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className={warning ? 'has-warning' : ''}
            placeholder="Enter value"
          />
          <div className="units-container">
            <span className="units">{currentUnit}</span>
            {hasOriginalUnits && (
              <button
                onClick={toggleUnits}
                className="unit-toggle"
                type="button"
                title={`Switch to ${useOriginal ? 'graph units' : 'original units'}`}
              >
                ⇄
              </button>
            )}
          </div>
        </div>
        
        {hasOriginalUnits && (
          <div className="unit-conversion-info">
            <span className="conversion-label">
              {useOriginal ? 'Original value:' : 'Graph value:'}
            </span>
            <span className="conversion-value">
              {useOriginal ? (
                <>As reported in PDF</>
              ) : (
                <>Normalized to {units}</>
              )}
            </span>
          </div>
        )}
        
        {warning && (
          <div className="editor-warning">
            <svg className="warning-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {warning}
          </div>
        )}
        
        {/* Show validation message for suspicious values */}
        {isSuspicious && validationMessage && (
          <div className="validation-warning">
            <svg className="warning-icon text-orange-500" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2C5.48 2 2 5.48 2 10s3.48 8 8 8 8-3.48 8-8-3.48-8-8-8zm1 13h-2v-2h2v2zm0-4h-2V7h2v4z"/>
            </svg>
            <span className="text-sm text-orange-700">{validationMessage}</span>
          </div>
        )}
        
        <div className="editor-actions">
          <button 
            onClick={handleSave} 
            className="save-btn"
            title="Save (Enter)"
          >
            <svg className="btn-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Save
          </button>
          {isSuspicious && onMarkAsCorrect && (
            <button 
              onClick={onMarkAsCorrect} 
              className="mark-correct-btn"
              title="Mark this value as correct and dismiss warning"
            >
              <svg className="btn-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Mark as Correct
            </button>
          )}
          <button 
            onClick={onCancel} 
            className="cancel-btn"
            title="Cancel (Esc)"
          >
            <svg className="btn-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default InlineValueEditor;